/** Route paths, named once so the router, the navigation and the tests cannot drift apart. */
export const HOME_PATH = '/';
export const ADMIN_PATH = '/admin';
export const ADMIN_PREINVITES_PATH = `${ADMIN_PATH}/preinvites`;

/**
 * The student register and one selected student (AR-11, OD-S1): the selection lives in the address,
 * so a row is shareable, reloadable and opens cold (`admin.students.get` reads one record by id).
 * `/admin/requests` is a retired address (OD-D2.10b: registration requests are handled on Admin
 * Home); the router redirects it there.
 */
export const ADMIN_REQUESTS_PATH = `${ADMIN_PATH}/requests`;
export const ADMIN_STUDENTS_PATH = `${ADMIN_PATH}/students`;
export const ADMIN_STUDENT_SEGMENT = ':accountId';

export function adminStudentPath(accountId: string): string {
  return `${ADMIN_STUDENTS_PATH}/${encodeURIComponent(accountId)}`;
}

/**
 * The administrator Q&A workspace, and one selected question inside it (T049, D-ROUTE).
 *
 * The selection lives in the address rather than in component state, so a queue row can be shared,
 * reloaded and opened cold: `/admin/qa/:questionId` loads that question itself instead of depending
 * on a queue page that was never fetched.
 */
export const ADMIN_QA_PATH = `${ADMIN_PATH}/qa`;
export const ADMIN_QA_QUESTION_SEGMENT = ':questionId';

export function adminQaQuestionPath(questionId: string): string {
  return `${ADMIN_QA_PATH}/${encodeURIComponent(questionId)}`;
}

/**
 * The course and group administration panels — T056 (FR-PANEL-01 (3) and (4)).
 *
 * The selection and the open tab both live in the address rather than in component state, so a course
 * or a group can be shared, reloaded and opened cold. `R2 SCREEN-MAP.md` states that its `#admin/...`
 * hashes are local reference examples and not mandated production route strings; these are the
 * production ones.
 *
 * A group is addressed *inside* its course because the accepted contract has no `admin.groups.get`:
 * the only bounded way to load one group is `admin.groups.list({ courseId })`, so the course is part
 * of the address rather than something the panel would have to guess.
 */
export const ADMIN_COURSES_PATH = `${ADMIN_PATH}/courses`;
export const ADMIN_GROUPS_PATH = `${ADMIN_PATH}/groups`;
export const ADMIN_COURSE_GROUP_SEGMENT = ':courseId/groups/:groupId/:tab';

/**
 * The Course Studio's tabs — AR-08.
 *
 * `DESIGN.md` § Navigation makes the course the principal context: "Course-owned work uses a Course
 * Studio: a searchable course context above tabs for overview, groups, schedule, materials and
 * students." The three T056 tabs map onto five: `content` becomes `materials`, `settings` becomes
 * `overview`, `groups` is unchanged, and `schedule` and `students` are new — the reusable course
 * template, and the course's own roster across all of its groups.
 *
 * The tab is part of the address, so a link to a course's materials opens the materials rather than a
 * screen that has to be clicked into.
 */
export const ADMIN_COURSE_TABS = Object.freeze([
  'overview',
  'groups',
  'schedule',
  'materials',
  'students',
] as const);

/**
 * Where a T056 address goes now — AR-08.
 *
 * Every existing administrator route stays reachable (the redesign's own acceptance boundary), and a
 * link an administrator bookmarked, an audit row, or a test written against the old names must not
 * land on "page not found". These two are redirected rather than kept as aliases: an alias would
 * leave two addresses for one screen, and the browser history would remember the one nobody chose.
 */
export const LEGACY_ADMIN_COURSE_TABS: Readonly<Record<string, AdminCourseTab>> = Object.freeze({
  content: 'materials',
  settings: 'overview',
});
/**
 * *(T056 WP4)* `students` is the group roster (R2-05).
 *
 * It is a tab of the group rather than a top-level panel because an enrolment is only ever read in
 * the context of the group that owns it, and because the accepted contract reaches a roster through
 * `admin.students.list({ groupId })` — a group-scoped read, not a directory of its own.
 */
export const ADMIN_GROUP_TABS = Object.freeze([
  'overview',
  'students',
  'schedule',
  'materials',
  'template',
  'settings',
] as const);

export type AdminCourseTab = (typeof ADMIN_COURSE_TABS)[number];
export type AdminGroupTab = (typeof ADMIN_GROUP_TABS)[number];

/**
 * *(OD-CS1, AD-6)* The Claude Design Course Studio's six tabs (`Simple-QA-Course-Studio-Admin`), each
 * its own address; the selected row of a tab lives in the query (`?lecture=`, `?material=`, …).
 */
export const COURSE_STUDIO_TABS = Object.freeze([
  'overview',
  'lectures',
  'groups',
  'materials',
  'students',
  'questions',
] as const);

export type CourseStudioTab = (typeof COURSE_STUDIO_TABS)[number];

/** Where an older Studio tab name leads on the design Studio (`schedule` was the lecture template). */
export const LEGACY_COURSE_STUDIO_TABS: Readonly<Record<string, CourseStudioTab>> = Object.freeze({
  content: 'materials',
  settings: 'overview',
  schedule: 'lectures',
});

export function adminCoursePath(courseId: string, tab: AdminCourseTab | CourseStudioTab = 'overview'): string {
  return `${ADMIN_COURSES_PATH}/${encodeURIComponent(courseId)}/${tab}`;
}

/** *(OD-CS1)* The Studio segment: the course and its tab, the tab optional (a bare course opens its overview). */
export const COURSE_STUDIO_SEGMENT = ':courseId/:tab?';

/**
 * *(OD-G, Claude Design Groups)* One group and its tab, under the Groups rail rather than inside its
 * course: the design's Groups page is a master-detail workspace (`Simple-QA-Admin-Groups`). The course
 * stays in the address because a group is found through `admin.groups.list` (there is no
 * `admin.groups.get`). The whole register and one group are one screen — optional segments — so
 * choosing a row keeps the page's filters and scroll.
 */
export const ADMIN_GROUP_SEGMENT = ':courseId?/:groupId?/:tab?';

export function adminGroupPath(
  courseId: string,
  groupId: string,
  tab: AdminGroupTab = 'overview',
): string {
  return `${ADMIN_GROUPS_PATH}/${encodeURIComponent(courseId)}/${encodeURIComponent(groupId)}/${tab}`;
}

/**
 * Where a tab of the old course-nested group address (`/admin/courses/:courseId/groups/:groupId/:tab`)
 * leads now: its `settings` was the schedule form, which is the design's „განრიგი“ tab.
 */
export const LEGACY_ADMIN_GROUP_TABS: Readonly<Record<string, AdminGroupTab>> = Object.freeze({
  overview: 'overview',
  students: 'students',
  settings: 'schedule',
});

/**
 * The retired operational addresses (T071): the action history, the batch pages and the system status
 * are not part of the design product (OD-D2.10b). They are named only so the router can redirect
 * them — and any identifier under them — to Admin Home.
 */
export const ADMIN_AUDIT_PATH = `${ADMIN_PATH}/audit`;
export const ADMIN_BATCHES_PATH = `${ADMIN_PATH}/batches`;
export const ADMIN_SYSTEM_PATH = `${ADMIN_PATH}/system`;

/**
 * The student course surfaces — T062-B (D9), FR-MAT-04.
 *
 * A material carries its **complete** nested address for the same reason every protected-file
 * operation does: `materials.fileAccess` takes `{courseId, sectionId, materialId}`, so a bare
 * material id names nothing the server can address, and a reader opened from a cold link must be able
 * to ask for its bytes without a list having been fetched first.
 *
 * `SCREEN-MAP.md` states that its `#material/m3` hashes are local reference examples and not mandated
 * production route strings; these are the production ones.
 */
export const COURSES_PATH = '/courses';
export const COURSE_SEGMENT = ':courseId';
export const COURSE_MATERIAL_SEGMENT = ':courseId/materials/:sectionId/:materialId';

export function coursePath(courseId: string): string {
  return `${COURSES_PATH}/${encodeURIComponent(courseId)}`;
}

export function materialPath(courseId: string, sectionId: string, materialId: string): string {
  return `${coursePath(courseId)}/materials/${encodeURIComponent(sectionId)}/${encodeURIComponent(materialId)}`;
}

/**
 * The student profile — T056 WP4 (FR-PROF-04, FR-ENT-06).
 *
 * Its own address rather than a card on the course library: it is the only surface that shows the
 * exact entitlement end, and the only one that lists an enrolment `courses.listMine` omits — a closed
 * one, or one whose course has not opened yet. `SCREEN-MAP.md` states that its `#profile` hash is a
 * local reference example and not a mandated production route string; this is the production one.
 */
export const PROFILE_PATH = '/profile';

/** The student question-and-answer surface, and the permalink of one question (FR-SQA-06). */
export const QA_PATH = '/qa';
export const QA_QUESTION_SEGMENT = ':questionId';

export function qaQuestionPath(questionId: string): string {
  return `${QA_PATH}/${encodeURIComponent(questionId)}`;
}
